import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import Footer from "../../components/footer";
import Header from "../../components/header";
import SeoHead from "../../components/seoHead";
import TrendingMarketPanel from "../../components/trendingMarketPanel";
import { LocalizedLink, useAppLocale } from "../../i18n/LocaleRouting";
import {
  getOtherTrendingItemsByProductId,
  getTrendingData,
  getTrendingItemByProductId,
} from "../../services/getTrendingData";
import { TrendingHottestItem } from "../../shared/type";
import {
  buildTrendingItemSeo,
  trendingItemFallbackPath,
} from "../../utils/trendingItemSeo";
import {
  extractTrendingProductId,
  matchesTrendingProductId,
  trendingItemPathFromItem,
} from "../../utils/trendingItemId";

export default function TrendingItem() {
  const locale = useAppLocale();
  const { productId } = useParams<{ productId: string }>();
  const [item, setItem] = useState<TrendingHottestItem | null>(() =>
    getTrendingItemByProductId(productId),
  );
  const [otherItems, setOtherItems] = useState<TrendingHottestItem[]>(() =>
    getOtherTrendingItemsByProductId(productId),
  );
  const [loading, setLoading] = useState(
    () => !getTrendingItemByProductId(productId),
  );

  useEffect(() => {
    const archivedOrCached = getTrendingItemByProductId(productId);
    setOtherItems(getOtherTrendingItemsByProductId(productId));

    if (archivedOrCached) {
      setItem(archivedOrCached);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    // Between deploys: try latest hottest fetch (not yet merged into archive).
    getTrendingData().then((data) => {
      if (cancelled) return;
      const matched =
        data.find((entry) => matchesTrendingProductId(entry, productId)) ??
        null;
      setItem(matched);
      setOtherItems(getOtherTrendingItemsByProductId(productId));
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [productId]);

  const fallbackPath = trendingItemFallbackPath(productId);

  if (loading) {
    return (
      <>
        <SeoHead
          title="Trending Item"
          description="Loading trending trading card item."
          path={fallbackPath}
          noindex
        />
        <Header />
        <div className="w-full min-h-screen flex justify-center items-center bg-white text-slate-600">
          Loading...
        </div>
      </>
    );
  }

  if (!item) {
    return (
      <>
        <SeoHead
          title="Trending Item Not Found"
          description="This trending trading card item does not exist or is no longer available."
          path={fallbackPath}
          noindex
        />
        <Header />
        <div className="w-full min-h-screen flex flex-col justify-center items-center gap-4 bg-white px-6">
          <h1 className="text-2xl font-bold text-slate-900">Not Found</h1>
          <LocalizedLink to="/" className="text-red-500 hover:text-red-400 underline">
            Back to Home
          </LocalizedLink>
        </div>
      </>
    );
  }

  const seo = buildTrendingItemSeo(item, otherItems, locale);

  return (
    <>
      <SeoHead {...seo} />
      <Header />
      <main className="w-full flex justify-center bg-white">
        <div className="w-full max-w-[1600px] md:px-14 md:py-10 p-8 flex flex-col items-center gap-y-8">
          <article className="w-full max-w-[80%] flex flex-col lg:flex-row gap-4 items-center justify-center">
            <img
              src={item.thumbnailUrl}
              alt={item.name}
              className="w-full max-w-[240px] md:max-w-[320px] aspect-square object-cover rounded-lg self-center"
            />
            <div className="flex flex-col gap-3">
              <h1 className="text-base md:text-xl font-bold text-slate-900">
                {item.name}
              </h1>
              {item.minPriceFormat ? (
                <p className="text-base md:text-lg font-bold text-slate-700">
                  {item.minPriceFormat}
                </p>
              ) : null}
            </div>
          </article>
          <span className="w-full bg-white border-t border-slate-200"></span>

          {productId ? <TrendingMarketPanel productId={productId} /> : null}

          {otherItems.length > 0 ? (
            <section
              className="w-full bg-white border-t border-slate-200"
              aria-labelledby="more-trending-heading"
            >
              <div className="w-full max-w-[1600px] mx-auto md:px-14 py-6">
                <h2
                  id="more-trending-heading"
                  className="text-base md:text-lg font-bold text-slate-900 mb-6 px-4 md:px-0"
                >
                  More Trending
                </h2>
                {/* < md：横向滑动；md+：网格 */}
                <div className="-mr-8 overflow-x-auto scrollbar-hide md:mx-0 md:overflow-visible">
                  <div
                    className="
                      flex w-max gap-3 px-4 snap-x snap-mandatory
                      sm:gap-4
                      md:w-full md:grid md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6
                      md:gap-10 md:px-0 md:snap-none
                    "
                  >
                    {otherItems.map((entry) => {
                      const entryProductId =
                        extractTrendingProductId(entry.productNumber) ??
                        String(entry.id);

                      return (
                        <LocalizedLink
                          key={entryProductId}
                          target="_top"
                          to={trendingItemPathFromItem(entry)}
                          className="
                          flex-shrink-0 w-[132px] snap-start group flex flex-col
                          sm:w-[148px]
                          md:w-full
                        "
                        >
                          <div className="w-full aspect-square bg-[#f0f0f0]/60 rounded-md overflow-hidden flex items-center justify-center">
                            <img
                              src={entry.thumbnailUrl}
                              alt={entry.name}
                              draggable={false}
                              onDragStart={(e) => e.preventDefault()}
                              className="w-full h-full object-cover p-2 group-hover:scale-105 transition-transform duration-200 select-none [-webkit-user-drag:none]"
                            />
                          </div>
                          <p className="mt-2 text-xs text-slate-900 line-clamp-2 leading-snug">
                            {entry.name}
                          </p>
                          {entry.minPriceFormat ? (
                            <p className="mt-1 text-xs font-bold text-slate-900">
                              {entry.minPriceFormat}
                            </p>
                          ) : null}
                        </LocalizedLink>
                      );
                    })}
                  </div>
                </div>
              </div>
            </section>
          ) : null}
        </div>
      </main>

      <Footer />
    </>
  );
}
